import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Shield, Clock, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';

// SEO page configurations
const SEO_PAGES = {
  '/hvac-pricing-new-orleans': {
    h1: 'HVAC Pricing New Orleans',
    title: 'HVAC Pricing New Orleans | Get Instant Quotes',
    description: 'Find accurate HVAC pricing in New Orleans. Get instant estimates for AC replacement, heating installation, and system upgrades. No waiting, no pressure.',
    intro: 'Looking for transparent HVAC pricing in New Orleans? Our instant estimate tool provides real pricing for all your heating and cooling needs.',
    features: [
      'AC replacement pricing',
      'Heating system installation costs',
      'Heat pump upgrade estimates',
      'Ductless mini-split pricing',
    ],
  },
  '/ac-replacement-cost-new-orleans': {
    h1: 'AC Replacement Cost New Orleans',
    title: 'AC Replacement Cost New Orleans | Free Instant Estimate',
    description: 'Get accurate AC replacement costs in New Orleans. See real pricing for new air conditioning units. Instant online estimates available.',
    intro: 'Wondering about AC replacement costs in New Orleans? Our online tool provides instant estimates based on your home size and system preferences.',
    features: [
      'Central AC replacement',
      'High-efficiency AC systems',
      'Ductless AC installation',
      'AC upgrade options',
    ],
  },
  '/hvac-estimate-metairie': {
    h1: 'HVAC Estimate Metairie',
    title: 'HVAC Estimate Metairie | Instant Online Quotes',
    description: 'Get your instant HVAC estimate in Metairie. See real pricing for AC replacement, heating installation, and system upgrades in minutes.',
    intro: 'Metairie homeowners can now get instant HVAC estimates online. No waiting for contractor visits—see your pricing in minutes.',
    features: [
      'AC replacement estimates',
      'Heating system pricing',
      'Heat pump installations',
      'Full HVAC system quotes',
    ],
  },
  '/hvac-estimate-kenner': {
    h1: 'HVAC Estimate Kenner',
    title: 'HVAC Estimate Kenner | Free Instant Quote',
    description: 'Get your instant HVAC estimate in Kenner. Real pricing for AC replacement and HVAC installation. No phone call required.',
    intro: 'Kenner residents can get instant HVAC estimates without waiting. Our online tool provides accurate pricing for all your heating and cooling needs.',
    features: [
      'AC replacement costs',
      'New HVAC installation',
      'System upgrade pricing',
      'Energy-efficient options',
    ],
  },
};

const SEOPage = () => {
  const location = useLocation();
  const pageData = SEO_PAGES[location.pathname];

  if (!pageData) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {pageData.h1}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            {pageData.intro}
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Get My Instant Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What You Can Estimate
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {pageData.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="font-medium text-[#0F172A]">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="p-6 bg-white rounded-xl">
              <Clock className="w-8 h-8 text-[#0EA5E9] mx-auto mb-3" />
              <h3 className="font-bold text-[#0F172A] mb-1">No Phone Call Required</h3>
              <p className="text-sm text-slate-500">Get your estimate online instantly</p>
            </div>
            <div className="p-6 bg-white rounded-xl">
              <DollarSign className="w-8 h-8 text-[#0EA5E9] mx-auto mb-3" />
              <h3 className="font-bold text-[#0F172A] mb-1">Free Estimate in Minutes</h3>
              <p className="text-sm text-slate-500">See real pricing right away</p>
            </div>
            <div className="p-6 bg-white rounded-xl">
              <Shield className="w-8 h-8 text-[#0EA5E9] mx-auto mb-3" />
              <h3 className="font-bold text-[#0F172A] mb-1">Trusted Local Contractors</h3>
              <p className="text-sm text-slate-500">Vetted professionals only</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Get an Instant HVAC Estimate?
          </h2>
          
          <div className="prose prose-slate max-w-none">
            <p>
              Homeowners searching for an <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9]">instant HVAC estimate</a> can 
              now get real pricing online without waiting days for a contractor visit. Our platform provides 
              accurate <a href="/hvac-pricing-new-orleans" className="text-[#0EA5E9]">HVAC pricing online</a>, allowing you to compare options and receive 
              an instant AC quote in minutes.
            </p>
            
            <p>
              Whether you need an <a href="/ac-replacement-cost-new-orleans" className="text-[#0EA5E9]">AC replacement estimate</a> or 
              full system pricing, our tool gives you the information you need to make informed decisions about your home comfort.
            </p>

            <p>
              Built for Louisiana homes, our estimates factor in local pricing, humidity considerations, and 
              the specific needs of Gulf Coast climate control.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready for Your Free Estimate?
          </h2>
          <p className="text-slate-300 mb-8">
            Get accurate HVAC pricing in minutes. No obligation, no pressure.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Start My Instant Estimate
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SEOPage;
